import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { AppButton } from '../atoms';
import { useAppDispatch } from '../../redux/stateHooks';
import { APP_CONFIG } from '../../utility';
import { APP_ROUTES } from '../../constants';
import { logOut } from '../../redux/action/auth.action';
import { resetForm } from '../../redux/slices/teacherFormSliceModel';
import { resetSubscriptionState } from '../../redux/slices/subscriptionSliceModel';
import { useTranslation } from 'react-i18next';

const PROMPT_COUNTDOWN = Number(APP_CONFIG.SESSION_TIMEOUT_PROMPT_COUNTDOWN);
const INACTIVITY_TIMEOUT = Number(APP_CONFIG.SESSION_TIMEOUT) * 60 * 1000;

type UseIdleTimerProps = {
  timeout: number;
  onPrompt?: () => void;
  onIdle?: () => void;
};

function useIdleTimer({ timeout, onPrompt, onIdle }: UseIdleTimerProps) {
  const timerId = useRef(null);
  const events = useRef(['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll']);
  const idle = useRef(false);

  const clear = () => {
    if (timerId.current) {
      clearTimeout(timerId.current);
      timerId.current = null;
    }
  };

  const reset = useCallback(() => {
    clear();
    idle.current = false;
    timerId.current = setTimeout(() => {
      idle.current = true;
      onPrompt && onPrompt();
    }, timeout);
  }, [timeout, onPrompt]);

  useEffect(() => {
    const handleActivity = () => {
      if (!idle.current) {
        reset();
      }
    };
    events.current.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });
    reset();
    return () => {
      clear();
      events.current.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [reset]);

  const triggerIdle = useCallback(() => {
    clear();
    onIdle && onIdle();
  }, [onIdle]);

  return { reset, triggerIdle };
}

type SessionTimeoutProps = {
  children: React.ReactNode;
};

const SessionTimeout = ({ children }: SessionTimeoutProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [countdown, setCountdown] = useState(PROMPT_COUNTDOWN);
  const countdownRef = useRef(null);
  const idleTimer = useRef(null);

  const handlePrompt = useCallback(() => {
    setIsPromptVisible(true);
    setCountdown(PROMPT_COUNTDOWN);
  }, []);

  const handleIdle = useCallback(() => {
    dispatch(logOut())
      .unwrap()
      .then(() => {
        localStorage.clear();
        sessionStorage.clear();
        dispatch(resetForm());
        dispatch(resetSubscriptionState());
        window.location.replace(APP_ROUTES.LOGIN);
      })
      .catch((error: any) => {
        console.error('Error during sign-out:', error);
      });
  }, [dispatch]);

  // Setup idle timer
  idleTimer.current = useIdleTimer({
    timeout: INACTIVITY_TIMEOUT,
    onPrompt: handlePrompt,
    onIdle: handleIdle,
  });

  // Countdown logic for modal
  useEffect(() => {
    if (isPromptVisible) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [isPromptVisible]);

  // When countdown hits 0, logout
  useEffect(() => {
    if (isPromptVisible && countdown <= 0) {
      setIsPromptVisible(false);
      idleTimer.current.triggerIdle();
    }
  }, [countdown, isPromptVisible]);

  // Handler for "Stay Logged In"
  const handleStayLoggedIn = () => {
    setIsPromptVisible(false);
    setCountdown(PROMPT_COUNTDOWN);
    idleTimer.current.reset();
  };

  return (
    <>
      <Dialog
        visible={isPromptVisible}
        header={t('dialog.session_timeout_warning', "Session Timeout Warning")}
        closable={false}
        modal
        style={{ width: '350px' }}
        onHide={() => setIsPromptVisible(false)}
        footer={
          <AppButton
            className="p-button p-component p-button-primary w-full mt-2"
            onClick={handleStayLoggedIn}
            type="button"
            label={t('common.button_stay_logged_in', 'Stay Logged In')}
          />
        }
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-base font-medium text-gray-800">
            {t('g.inactive_logout_warning', "You've been inactive. You will be logged out in")}
            <span className="text-primary font-bold mx-1">{countdown}</span>
            {t('g.n_seconds_suffix', 'seconds.')}
          </span>
        </div>
      </Dialog>
      {children}
    </>
  );
};

export default SessionTimeout;
